import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { UsersIcon } from '@heroicons/react/24/outline';
import FormInput from '../../../../components/FormInput';
import ToggleSwitch from '../../../../components/ToggleSwitch';
import QuestionMarkTooltip from '../../../../components/QuestionMarkTooltip';
import Pagination from '../../../../components/Pagination';
import {useBreadcrumbs} from '../../../../contexts/BreadcrumbContext';
import useSelectedResource from '../../../../hooks/useSelectedResource';
import {
  fetchResourceParticipants,
  updateParticipationType,
  updateParticipantLocally,
  selectParticipants,
  selectParticipantsLoading,
  selectParticipantsSummary,
} from '../../../../slices/userSlice';
import { selectBlurSensitive } from '../../../../slices/preferencesSlice';
import {
  selectAuthIsAdmin,
  selectAuthIsMarketOwner,
  selectAuthIsMarketOwnerReadOnly,
  selectAuthIsForecaster,
} from '../../../../slices/authSlice';
import PermissionChecker from '../../../../utils/permissionChecker';

export default function ResourceParticipants() {
  useBreadcrumbs([
    { label: 'Forecasters', href: '/data/resource/participants' },
    { label: 'Participants' }
  ], 'Manage forecaster participation and access to resources');
  const dispatch = useDispatch();
  const selectedResource = useSelectedResource();
  const participants = useSelector(selectParticipants);
  const loading = useSelector(selectParticipantsLoading);
  const summary = useSelector(selectParticipantsSummary);
  const blurSensitive = useSelector(selectBlurSensitive);

  // Permission checking
  const isAdmin = useSelector(selectAuthIsAdmin);
  const isMarketOwner = useSelector(selectAuthIsMarketOwner);
  const isMarketOwnerReadOnly = useSelector(selectAuthIsMarketOwnerReadOnly);
  const isForecaster = useSelector(selectAuthIsForecaster);

  const permissionChecker = new PermissionChecker({
    isAdmin,
    isMarketOwner,
    isMarketOwnerReadOnly,
    isForecaster,
  });

  // Can edit participation type if user is market_owner or admin
  const canEditParticipation = permissionChecker.satisfies(['market_owner', 'admin']);

  const [search, setSearch] = useState('');
  const [filtered, setFiltered] = useState([]);
  const [updatingIds, setUpdatingIds] = useState(new Set());
  const [filterType, setFilterType] = useState('all'); // 'all', 'performance', 'fixed'
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  useEffect(() => {
    if (selectedResource?.id) {
      dispatch(fetchResourceParticipants(selectedResource.id));
    }
  }, [dispatch, selectedResource?.id]);

  useEffect(() => {
    const q = (search || '').trim().toLowerCase();

    let result = participants;

    // Apply participation type filter
    if (filterType === 'performance') {
      result = result.filter(p => !p.is_fixed_payment);
    } else if (filterType === 'fixed') {
      result = result.filter(p => p.is_fixed_payment);
    }

    // Apply search filter
    if (q) {
      result = result.filter(
        (p) =>
          (p.user_email || '').toLowerCase().includes(q) ||
          (p.user_name || '').toLowerCase().includes(q)
      );
    }

    setFiltered(result);
    setCurrentPage(1); // Reset to page 1 when filters change
  }, [participants, search, filterType]);

  // Pagination calculation
  const totalItems = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const paginatedParticipants = filtered.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) return;
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };


  const handleToggleParticipationType = async (participant) => {
    const newValue = !participant.is_fixed_payment;
    const participantId = participant.id;

    // Add to updating set
    setUpdatingIds((prev) => new Set(prev).add(participantId));

    try {
      const result = await dispatch(
        updateParticipationType({
          participantId,
          is_fixed_payment: newValue,
          resourceId: selectedResource?.id,
        })
      );

      if (updateParticipationType.rejected.match(result)) {
        toast.error(result.payload || 'Failed to update contract type');
        // Remove from updating set on error
        setUpdatingIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(participantId);
          return newSet;
        });
        return;
      }

      const contractType = newValue ? 'Fixed Contract' : 'Performance-Based';
      toast.success(`Contract type updated to ${contractType} for ${participant.user_name}`);

      // Optimistically update the participants list WITHOUT refetching
      // This prevents the page reload
      dispatch(updateParticipantLocally({
        participantId,
        is_fixed_payment: newValue,
      }));

    } catch (err) {
      console.error(err);
      toast.error('An error occurred while updating');
    } finally {
      // Remove from updating set
      setUpdatingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(participantId);
        return newSet;
      });
    }
  };

  if (!selectedResource) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-center">
        <UsersIcon className="h-16 w-16 text-gray-400 dark:text-gray-600 mb-4" />
        <h2 className="text-xl font-semibold text-gray-700 dark:text-gray-300 mb-2">
          No Resource Selected
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400">
          Please select a resource from the dropdown above to view and manage participants
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        {/* Left Column - Summary Panel */}
        <div className="col-span-1 rounded-lg bg-surface dark:bg-gray-870 p-6 shadow-sm border border-gray-200/70 dark:border-gray-600/70">
          <div className="flex flex-col gap-3">
            {/* Resource Info */}
            <div className="flex items-start justify-between">
              <div className="min-w-0">
                <div className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                  {selectedResource.name}
                </div>
                <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Resource
                </div>
              </div>
            </div>

            {/* Participation Summary Stats */}
            {summary && (
              <div className="mt-2 space-y-3 text-sm text-gray-700 dark:text-gray-300">
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Total Participants</div>
                  <div className="mt-1 text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {summary.total_participants}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Performance-Based</div>
                  <div className="mt-1 text-3xl font-bold text-brand-600 dark:text-brand-400">
                    {summary.performance_based_count}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">Fixed Contract</div>
                  <div className="mt-1 text-3xl font-bold text-gray-900 dark:text-gray-100">
                    {summary.fixed_payment_count}
                  </div>
                </div>
              </div>
            )}

            {/* Active Filter Info */}
            {(filterType !== 'all' || search) && (
              <div className="mt-3">
                <div className="text-xs text-gray-500 dark:text-gray-400">Active Filters</div>
                <div className="mt-1 space-y-1">
                  {filterType !== 'all' && (
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      Type: <span className="font-medium">{filterType === 'performance' ? 'Performance-Based' : 'Fixed Contract'}</span>
                    </div>
                  )}
                  {search && (
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      Search: <span className="font-medium">"{search}"</span>
                    </div>
                  )}
                  <div className="text-sm text-brand-600 dark:text-brand-400">
                    {filtered.length} result{filtered.length !== 1 ? 's' : ''}
                  </div>
                </div>
              </div>
            )}

            {/* Permission Notice / Helper Text */}
            <div className="mt-3">
              {!canEditParticipation ? (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-md">
                  <div className="flex items-start gap-2">
                    <svg className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    <div className="text-xs text-amber-800 dark:text-amber-200">
                      <span className="font-medium">Read-Only Access:</span> You can view participant information but cannot modify contract types. Only Market Owners and Admins can edit participation settings.
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-gray-500 dark:text-gray-400">
                  Toggle between Performance-Based and Fixed Contract payment types for each participant.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Interactive Content */}
        <div className="col-span-1 lg:col-span-2 space-y-6">

      {/* Filter and Search Bar */}
      <div className="bg-surface dark:bg-gray-870 p-4 rounded-lg border border-gray-200/70 dark:border-gray-600/70 shadow-sm space-y-4">
        {/* Filter Buttons */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setFilterType('all')}
            className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
              filterType === 'all'
                ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300'
                : 'border-gray-200/70 dark:border-gray-600/70 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            All Participants
          </button>
          <button
            onClick={() => setFilterType('performance')}
            className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
              filterType === 'performance'
                ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300'
                : 'border-gray-200/70 dark:border-gray-600/70 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            Performance-Based
          </button>
          <button
            onClick={() => setFilterType('fixed')}
            className={`px-4 py-2 rounded-md border text-sm font-medium transition-colors ${
              filterType === 'fixed'
                ? 'border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300'
                : 'border-gray-200/70 dark:border-gray-600/70 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            Fixed Contract
          </button>
        </div>

        {/* Search Input */}
        <FormInput
          leftIcon="search"
          size="large"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email..."
        />
      </div>



          {/* Participants List */}
          <div className="bg-surface dark:bg-gray-870 rounded-lg border border-gray-200/70 dark:border-gray-600/70 shadow-sm">
            {loading ? (
              <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600 mx-auto mb-2"></div>
                Loading participants...
              </div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center">
                <UsersIcon className="h-12 w-12 text-gray-400 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400">
                  {search ? 'No participants match your search' : 'No participants yet'}
                </p>
                {!search && (
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">
                    Participants will appear here after their first forecast submission
                  </p>
                )}
              </div>
            ) : (
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {paginatedParticipants.map((p) => {
                  const isUpdating = updatingIds.has(p.id);
                  // user_email/user_name are superuser-only (audit A260528-API-H2);
                  // fall back to the username pseudonym so non-SU views never crash.
                  const displayName = p.user_name || p.user_username || p.user_email || '?';
                  const initials = displayName
                    .split(' ')
                    .map((n) => n[0] || '')
                    .join('')
                    .toUpperCase()
                    .substring(0, 2) || '?';

                  return (
                    <div
                      key={p.id}
                      className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                    >
                      {/* Mobile: Vertical layout */}
                      <div className="sm:hidden space-y-3">
                        {/* User Info */}
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-brand-100 dark:bg-brand-900 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-brand-600 dark:text-brand-400">
                              {initials}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className={`font-medium text-gray-900 dark:text-gray-100 truncate ${blurSensitive ? 'blur-sm select-none' : ''}`}>
                              {displayName}
                            </div>
                            {p.user_email && (
                              <div className={`text-sm text-gray-500 dark:text-gray-400 truncate ${blurSensitive ? 'blur-sm select-none' : ''}`}>
                                {p.user_email}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Contract Type Toggle */}
                        <div className="flex items-center justify-between pl-13">
                          <span className="text-sm text-gray-700 dark:text-gray-300">Contract Type:</span>
                          <div className="flex items-center gap-2">
                            <span className={`text-xs ${p.is_fixed_payment ? 'text-gray-400 dark:text-gray-500' : 'font-medium text-brand-600 dark:text-brand-400'}`}>
                              Performance
                            </span>
                            <ToggleSwitch
                              checked={p.is_fixed_payment}
                              onChange={() => handleToggleParticipationType(p)}
                              disabled={isUpdating || !canEditParticipation}
                            />
                            <span className={`text-xs ${!p.is_fixed_payment ? 'text-gray-400 dark:text-gray-500' : 'font-medium text-brand-600 dark:text-brand-400'}`}>
                              Fixed
                            </span>
                            {isUpdating && (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600 ml-2"></div>
                            )}
                          </div>
                        </div>

                        {/* First Submission */}
                        {p.created_at && (
                          <div className="text-xs text-gray-400 dark:text-gray-500 pl-13">
                            First submission: {new Date(p.created_at).toLocaleDateString()}
                          </div>
                        )}
                      </div>

                      {/* Desktop: Horizontal layout */}
                      <div className="hidden sm:flex items-center justify-between gap-4">
                        {/* User Info */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="h-10 w-10 rounded-full bg-brand-100 dark:bg-brand-900 flex items-center justify-center flex-shrink-0">
                            <span className="text-sm font-semibold text-brand-600 dark:text-brand-400">
                              {initials}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className={`font-medium text-gray-900 dark:text-gray-100 truncate ${blurSensitive ? 'blur-sm select-none' : ''}`}>
                              {displayName}
                            </div>
                            {p.user_email && (
                              <div className={`text-sm text-gray-500 dark:text-gray-400 truncate ${blurSensitive ? 'blur-sm select-none' : ''}`}>
                                {p.user_email}
                              </div>
                            )}
                            {p.created_at && (
                              <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                                First submission: {new Date(p.created_at).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Toggle Switch */}
                        <div className="flex items-center gap-3 flex-shrink-0">
                          <div className="flex items-center gap-1">
                            <span className={`text-sm ${!canEditParticipation ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'}`}>Performance</span>
                            <QuestionMarkTooltip tooltipText="Earnings vary based on forecast accuracy and ranking" />
                          </div>
                          <ToggleSwitch
                            checked={p.is_fixed_payment}
                            onChange={() => handleToggleParticipationType(p)}
                            disabled={isUpdating || !canEditParticipation}
                          />
                          <div className="flex items-center gap-1">
                            <span className={`text-sm ${!canEditParticipation ? 'text-gray-400 dark:text-gray-500' : 'text-gray-500 dark:text-gray-400'}`}>Fixed</span>
                            <QuestionMarkTooltip tooltipText="Paid a fixed amount regardless of performance" />
                          </div>
                          {isUpdating && (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-brand-600"></div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Pagination - Left and Right Aligned */}
            {!loading && filtered.length > 0 && (
              <div className="p-4 border-t border-gray-200/70 dark:border-gray-600/70">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalItems}
                  itemsPerPage={pageSize}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={(newPageSize) => {
                    setPageSize(newPageSize);
                    setCurrentPage(1);
                  }}
                  itemsPerPageOptions={[5, 10, 25, 50, 100]}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
